import { useMemo } from 'react';
import { Node } from '@xyflow/react';

import { ExecutionDetails } from '@frontend/types/agentExecution';
import { StepExecution, StepExecutionStatus } from '@frontend/types/stepExecution';

import {
  getStepExecutionDetailsByStepId,
  getTotalArtifactCountForStep,
} from '../utils/executionUtils';

export type NodeWithExecutionStatus = Node & {
  data: Node['data'] & {
    executionStatus?: StepExecutionStatus;
    stepExecution?: StepExecution;
    isSelected?: boolean;
    executionCount?: number;
    artifactCount?: number;
  };
};

type UseStepExecutionsResult = {
  nodesWithStatus: NodeWithExecutionStatus[];
  stepExecutionsMap: Map<string, StepExecution>;
  selectedStepExecution: StepExecution | null;
};

export function useStepExecutions(
  nodes: Node[],
  executionDetails: ExecutionDetails | undefined,
  selectedStepId?: string
): UseStepExecutionsResult {
  return useMemo(() => {
    const stepExecutionsMap = new Map<string, StepExecution>();

    // Process step executions from execution details
    if (executionDetails?.step_executions) {
      (executionDetails.step_executions as StepExecution[]).forEach(stepExecution => {
        if (stepExecution.step_id) {
          stepExecutionsMap.set(stepExecution.step_id, stepExecution);
        }
      });
    }

    // Map nodes with execution status
    const nodesWithStatus: NodeWithExecutionStatus[] = nodes.map(node => {
      const stepExecution = stepExecutionsMap.get(node.id);

      // For ForLoop and other nodes that may have multiple executions, get all executions
      const allExecutions = executionDetails
        ? getStepExecutionDetailsByStepId(executionDetails, node.id)
        : [];
      const executionCount = allExecutions.length;

      // Always use the last execution's status for all nodes
      let executionStatus: StepExecutionStatus = 'pending';
      if (executionCount > 0) {
        executionStatus = allExecutions[allExecutions.length - 1]?.status || 'pending';
      }

      // Calculate total artifact count across all executions for this step
      // This recursively counts artifacts in nested executions (e.g., ForLoop iterations)
      const artifactCount = executionDetails
        ? getTotalArtifactCountForStep(executionDetails, node.id)
        : 0;

      return {
        ...node,
        data: {
          ...node.data,
          executionStatus,
          stepExecution,
          isSelected: selectedStepId === node.id,
          executionCount,
          artifactCount,
        },
      };
    });

    const selectedStepExecution = selectedStepId
      ? stepExecutionsMap.get(selectedStepId) || null
      : null;

    return {
      nodesWithStatus,
      stepExecutionsMap,
      selectedStepExecution,
    };
  }, [nodes, executionDetails, selectedStepId]);
}
